import React, { useEffect, useRef, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  TextField,
  CircularProgress,
  Typography,
  IconButton,
  Autocomplete,
} from "@mui/material";
import { EditOutlined, AddCircleOutline, Close } from "@mui/icons-material";
import { keyframes } from "@mui/system";
import { adminService } from "../services/adminService";
import {
  primaryDark,
  accentColor,
  buttonGradient,
  buttonGradientHover,
} from "../../../constants/consts";

const popIn = keyframes`
  from { opacity: 0; transform: scale(0.92); }
  to   { opacity: 1; transform: scale(1); }
`;

const USER_TYPES = [
  { value: "all", label: "Herkes" },
  { value: "user", label: "Kullanıcı" },
  { value: "rootUser", label: "Admin Müşteri" },
];

const userTypeLabel = (v) => USER_TYPES.find((t) => t.value === v)?.label || v;

const normalize = (s = "") => String(s).toLocaleLowerCase("tr-TR").trim();

const inputSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: 2,
    transition: "box-shadow 0.25s ease, background-color 0.25s ease",
    "&:hover": { backgroundColor: "rgba(4,87,129,0.02)" },
    "&.Mui-focused": { boxShadow: "0 0 0 4px rgba(4,87,129,0.10)" },
  },
};

export default function NotificationEditDialog({
  open,
  item,
  sunucuKoduList = [],
  onClose,
  onSaved,
}) {
  const [form, setForm] = useState({
    id: "",
    title: "",
    description: "",
    sunucuKodu: "Hepsi",
    userType: "all",
  });
  const [imageUrl, setImageUrl] = useState("");
  const [previewUrl, setPreviewUrl] = useState("");
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const fileRef = useRef(null);

  useEffect(() => {
    if (open && item) {
      setForm({
        id: item.id ?? "",
        title: item.title ?? "",
        description: item.description ?? "",
        sunucuKodu: item.sunucuKodu || "Hepsi",
        userType: item.userType || "all",
      });
      setImageUrl(item.image ?? "");
      setPreviewUrl(item.image ?? "");
      setFile(null);
      setError("");
    }
  }, [open, item]);

  const handleFile = (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setFile(f);
    setPreviewUrl(URL.createObjectURL(f));
  };

  const handleSave = async () => {
    if (!form.title.trim() || !form.description.trim()) {
      setError("Konu ve içerik zorunludur.");
      return;
    }
    if (!form.sunucuKodu) {
      setError("Sunucu kodu zorunludur.");
      return;
    }
    setSaving(true);
    setError("");

    try {
      let finalImage = imageUrl;

      if (file) {
        setUploading(true);
        const res = await adminService.uploadFile(
          file,
          "Nontification",
          "image",
        );
        setUploading(false);
        finalImage = res?.url || imageUrl;
      }

      await adminService.editNotification({
        id: form.id,
        title: form.title.trim(),
        description: form.description.trim(),
        image: finalImage,
        sunucuKodu: form.sunucuKodu,
        userType: form.userType,
      });

      onSaved?.();
      onClose?.();
    } catch (e) {
      console.error(e);
      setError("Duyuru güncellenemedi.");
    } finally {
      setSaving(false);
      setUploading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: {
          borderRadius: 3,
          animation: `${popIn} 0.25s cubic-bezier(0.22, 1, 0.36, 1) both`,
          overflow: "hidden",
        },
      }}
    >
      <Box
        sx={{
          px: 3,
          py: 1.75,
          background: `linear-gradient(135deg, ${primaryDark} 0%, ${accentColor} 100%)`,
        }}
      >
        <DialogTitle
          sx={{
            p: 0,
            color: "#fff",
            fontWeight: 700,
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          <EditOutlined sx={{ fontSize: 20 }} />
          Duyuruyu Düzenle
        </DialogTitle>
      </Box>

      <DialogContent sx={{ pt: 3 }}>
        {/* Resim */}
        <Box sx={{ display: "flex", justifyContent: "center", mb: 3 }}>
          <Box
            sx={{
              position: "relative",
              width: 140,
              height: 140,
              borderRadius: 2,
              backgroundColor: "#F8FAFC",
              border: "2px dashed rgba(4,87,129,0.20)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              overflow: "hidden",
            }}
          >
            {previewUrl ? (
              <Box
                component="img"
                src={previewUrl}
                alt="preview"
                sx={{ width: "100%", height: "100%", objectFit: "cover" }}
              />
            ) : (
              <Typography sx={{ color: "#94A3B8", fontSize: 12 }}>
                Resim yok
              </Typography>
            )}

            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              hidden
              onChange={handleFile}
            />

            <IconButton
              size="small"
              onClick={() => fileRef.current?.click()}
              disabled={saving}
              sx={{
                position: "absolute",
                bottom: 6,
                right: 6,
                backgroundColor: "rgba(255,255,255,0.95)",
                color: "#15803D",
                boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                "&:hover": { backgroundColor: "#fff" },
              }}
            >
              <AddCircleOutline sx={{ fontSize: 20 }} />
            </IconButton>

            {previewUrl && previewUrl !== imageUrl && (
              <IconButton
                size="small"
                onClick={() => {
                  setFile(null);
                  setPreviewUrl(imageUrl);
                }}
                sx={{
                  position: "absolute",
                  top: 6,
                  right: 6,
                  backgroundColor: "rgba(255,255,255,0.95)",
                  color: "#B91C1C",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                  "&:hover": { backgroundColor: "#fff" },
                }}
              >
                <Close sx={{ fontSize: 16 }} />
              </IconButton>
            )}
          </Box>
        </Box>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
            gap: 2,
          }}
        >
          <TextField
            label="Konu *"
            value={form.title}
            onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))}
            fullWidth
            size="small"
            sx={{ ...inputSx, gridColumn: { xs: "1", sm: "1 / span 2" } }}
          />
          <TextField
            label="Duyuru *"
            value={form.description}
            onChange={(e) =>
              setForm((p) => ({ ...p, description: e.target.value }))
            }
            fullWidth
            multiline
            minRows={4}
            maxRows={10}
            size="small"
            sx={{ ...inputSx, gridColumn: { xs: "1", sm: "1 / span 2" } }}
          />
          <Autocomplete
            size="small"
            options={sunucuKoduList}
            value={form.sunucuKodu || null}
            onChange={(_, v) => setForm((p) => ({ ...p, sunucuKodu: v || "" }))}
            filterOptions={(opts, { inputValue }) =>
              opts.filter((o) => normalize(o).includes(normalize(inputValue)))
            }
            noOptionsText="Sunucu bulunamadı"
            renderInput={(params) => (
              <TextField
                {...params}
                label="Sunucu Kodu *"
                placeholder="Yaz veya seç"
                sx={inputSx}
              />
            )}
          />
          <Autocomplete
            size="small"
            options={USER_TYPES.map((t) => t.value)}
            value={form.userType}
            onChange={(_, v) =>
              setForm((p) => ({ ...p, userType: v || "all" }))
            }
            getOptionLabel={userTypeLabel}
            disableClearable
            renderOption={(props, opt) => (
              <li {...props}>{userTypeLabel(opt)}</li>
            )}
            renderInput={(params) => (
              <TextField {...params} label="Kullanıcı Türü *" sx={inputSx} />
            )}
          />
        </Box>

        {error && (
          <Typography
            sx={{ mt: 2, color: "#B91C1C", fontSize: 12.5, fontWeight: 600 }}
          >
            {error}
          </Typography>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
        <Button
          onClick={onClose}
          variant="outlined"
          sx={{ textTransform: "none", borderRadius: 2 }}
        >
          Vazgeç
        </Button>
        <Button
          onClick={handleSave}
          disabled={saving || uploading}
          variant="contained"
          sx={{
            textTransform: "none",
            borderRadius: 2,
            fontWeight: 700,
            background: buttonGradient,
            "&:hover": { background: buttonGradientHover },
            "&:disabled": { background: "#c7c7c7" },
          }}
        >
          {saving || uploading ? (
            <CircularProgress size={20} sx={{ color: "#fff" }} />
          ) : (
            "Kaydet"
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
